/**
 * Version Checker Utility
 *
 * Detects when a new application version is deployed and prompts/forces reload
 * to prevent users from running stale cached code after rebuild.
 */

export class VersionChecker {
  private static readonly VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
  private static readonly VERSION_KEY = 'app_version_hash';
  private static currentVersionHash: string | null = null;
  private static checkIntervalId: number | null = null;

  /**
   * Initialize version checking
   * @param autoReload - If true, automatically reload on version change. If false, just log warning.
   */
  static initialize(autoReload = false): void {
    // Get initial version hash from main script tag
    this.currentVersionHash = this.getCurrentVersionHash();

    if (this.currentVersionHash) {
      // Store in localStorage
      const storedVersion = localStorage.getItem(this.VERSION_KEY);

      if (storedVersion && storedVersion !== this.currentVersionHash) {
        console.warn('New application version detected. Current version:', this.currentVersionHash);

        if (autoReload) {
          this.reloadApplication();
          return;
        }
      }

      localStorage.setItem(this.VERSION_KEY, this.currentVersionHash);
    }

    // Start periodic checking
    this.startPeriodicCheck(autoReload);
  }

  /**
   * Get current version hash from main script file
   */
  private static getCurrentVersionHash(): string | null {
    try {
      // Extract hash from main.js filename (e.g., main.abc123.js)
      const scripts = Array.from(document.getElementsByTagName('script'));
      const mainScript = scripts.find(
        (script) => script.src.includes('main') && script.src.includes('.js')
      );

      if (mainScript) {
        const match = mainScript.src.match(/main\.([a-f0-9]+)\.js/);
        return match ? match[1] : null;
      }

      return null;
    } catch (error) {
      console.error('Failed to extract version hash:', error);
      return null;
    }
  }

  /**
   * Start periodic version checking
   */
  private static startPeriodicCheck(autoReload: boolean): void {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
    }

    this.checkIntervalId = window.setInterval(() => {
      this.checkForNewVersion(autoReload);
    }, this.VERSION_CHECK_INTERVAL);
  }

  /**
   * Check if a new version is available by fetching index.html
   */
  private static async checkForNewVersion(autoReload: boolean): Promise<void> {
    try {
      // Fetch index.html with cache-busting
      const response = await fetch(`/index.html?v=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      if (!response.ok) return;

      const html = await response.text();

      // Extract main script hash from HTML
      const match = html.match(/main\.([a-f0-9]+)\.js/);
      const newVersionHash = match ? match[1] : null;

      if (newVersionHash && newVersionHash !== this.currentVersionHash) {
        console.warn(
          'New application version available. Stored:',
          this.currentVersionHash,
          'New:',
          newVersionHash
        );

        if (autoReload) {
          this.reloadApplication();
        } else {
          // Dispatch custom event for app to handle
          window.dispatchEvent(
            new CustomEvent('app-version-changed', {
              detail: {
                oldVersion: this.currentVersionHash,
                newVersion: newVersionHash,
              },
            })
          );
        }
      }
    } catch (error) {
      console.error('Version check failed:', error);
    }
  }

  /**
   * Force reload the application and clear all caches
   */
  static reloadApplication(): void {
    // Clear all caches
    this.clearAllCaches().then(() => {
      // Hard reload
      window.location.reload();
    });
  }

  /**
   * Clear all browser caches
   */
  private static async clearAllCaches(): Promise<void> {
    try {
      // Clear localStorage version
      localStorage.removeItem(this.VERSION_KEY);

      // Clear Service Worker caches if available
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
      }

      // Unregister service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));
      }
    } catch (error) {
      console.error('Failed to clear caches:', error);
    }
  }

  /**
   * Stop version checking
   */
  static stop(): void {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
  }
}
